import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useUserContext } from 'contexts/user';
import { useThemeContext } from 'contexts';
import TextField from 'components/control/text-field';
import TextArea from 'components/control/text-area';
import Button from 'components/control/button';
import { trackEvent } from 'utils/google-tag-manager';
import PlusIcon from 'components/icon/plus-icon';
import SuccessMessage from 'components/connection-request/success-message';
import Snackbar from 'components/snackbar';
import { createConnectionRequest } from 'data/connection-request';
import { buildClassName } from 'utils/build-class-name';
import { GTM_CLICK_APPRAISAL_DETAIL_ESTIMATE_FOR_FREE_POPUP, GTM_CLICK_APPRAISAL_ENTER_EMAIL, GTM_CLICK_APPRAISAL_NO_ESTIMATE_DETAIL_ESTIMATE_FOR_FREE_POPUP } from 'constants/events';
import styles from './style.module.scss';

import type Listing from 'data/listing';

export type ListingInfoType = Pick<Listing, 'isAvailable' | 'streetAddress' | 'id' | 'isOffMarket' | 'isRental' | 'addressPathWithFallback'>;

export interface HomeAppraisalConnectionRequestProps {
  /** The object containing information about the property. */
  listing: ListingInfoType;

  /** Determines whether the component is being displayed in a modal. Default is *false* */
  isInModal?: boolean;

  /** Determines whether the component is being displayed in a popup. Default is *false* */
  isInPopup?: boolean;

  /** Determines whether the component is being displayed within a no estimate appraisal result. Default is *false* */
  noEstimate?: boolean;
}

export default function HomeAppraisalConnectionRequest({ listing, isInModal, isInPopup, noEstimate }: HomeAppraisalConnectionRequestProps) {
  const { user, userLocation } = useUserContext();
  const { themeName } = useThemeContext();
  const { register, handleSubmit, formState: { errors }, setError, clearErrors, reset } = useForm();
  const [commentsPlaceholder, setCommentsPlaceholder] = useState('I\'d like to get an accurate home estimate of my property. (optional)');
  const [isShowingAdditionalComments, setIsShowingAdditionalComments] = useState(false);
  const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);
  const successfullySubmittedRef = useRef(false);

  const { streetAddress, id: listingId } = listing;

  const sendRequest = async (values: Record<string, unknown>) => {
    if (isInPopup) {
      trackEvent(noEstimate ? GTM_CLICK_APPRAISAL_NO_ESTIMATE_DETAIL_ESTIMATE_FOR_FREE_POPUP : GTM_CLICK_APPRAISAL_DETAIL_ESTIMATE_FOR_FREE_POPUP);
    }
    const sourceComments = values.sourceComments || 'I\'d like to get an accurate home estimate of my property.';
    const properties = {
      ...values,
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.homePhone || '',
      listingId,
      streetAddress,
      sourceComments,
      userCountry: userLocation?.countryCode,
      theme: themeName,
      isHomeAppraisal: true,
    };

    createConnectionRequest(properties)
      .then(() => {
        setSuccessfullySubmitted(true);
        reset();
      })
      .catch(({ errors }: { errors: { attribute: string; message: string }[] }) => {
        errors.forEach(({ attribute, message }) => setError(attribute, { message: message }));
      });
  };

  useEffect(() => {
    if (listing) {
      setCommentsPlaceholder(`I\'d like to get an accurate home estimate of my property, ${listing.streetAddress}. (optional)`);
    }
  }, [listing]);

  // Used to access state during component unmounting
  useEffect(() => {
    successfullySubmittedRef.current = successfullySubmitted;
  }, [successfullySubmitted]);

  return (
    <>
      <form
        onSubmit={e =>{
          clearErrors(); // clear manually before submit as we use custom error messages
          handleSubmit(sendRequest)(e);
        }}
        className={buildClassName(styles.component, isInModal && styles.modal)}
      >
        {!successfullySubmitted ?
          <>
            <h3>Get a more accurate, in-home estimate for free</h3>
            <p className={styles.note}>Connect with our team to get a detailed home estimate for free!</p>
            <TextField placeholder={'Enter Your Email'} {...register('email')} value={user?.email} errors={errors.email} onClick={() => trackEvent(GTM_CLICK_APPRAISAL_ENTER_EMAIL)} />
            {!isShowingAdditionalComments && <div onClick={() => setIsShowingAdditionalComments(true)} className={styles['add-comment']}><PlusIcon /> Add a Comment</div>}
            {isShowingAdditionalComments &&
              <TextArea
                placeholder={commentsPlaceholder}
                {...register('sourceComments')}
                errors={errors.sourceComments}
              />
            }
            <div className={styles['submit-button-container']}>
              <Button label="Get a Home Estimate for Free" type="submit" theme="primary" />
            </div>
          </>
          :
          <SuccessMessage />
        }
      </form>
      <Snackbar />
    </>
  );
}
